import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const LegacyRankChart = ({ selectedManager, legacyData = [], finalData = [], h2hData = [], className = "" }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const processLegacyRankData = () => {
      try {
        setLoading(true);
        setError(null);

        if (!legacyData || legacyData.length === 0) {
          throw new Error('No legacy data available');
        }

        if (!selectedManager) {
          throw new Error('No manager selected');
        }

        // Get all years and managers
        const allYears = [...new Set(legacyData.map(row => row.Year))].sort((a, b) => a - b);
        const allManagers = [...new Set(legacyData.map(row => row.Manager))];

        console.log('Legacy rank data processing:', {
          totalRecords: legacyData.length,
          years: allYears,
          managers: allManagers,
          selectedManager
        });

        // Calculate ranks for each year (following R pattern: group_by(Year) %>% mutate(Rank = order(order(LegacyScore, decreasing = TRUE))))
        const chartDataByYear = allYears.map(year => {
          const yearData = { Year: year };
          
          // Get all managers' scores for this year
          const yearScores = allManagers.map(manager => {
            const managerRecord = legacyData.find(row => row.Year === year && row.Manager === manager);
            return {
              manager,
              score: managerRecord ? (managerRecord.LegacyScore || 0) : null
            };
          }).filter(item => item.score !== null);

          // Sort by score descending and assign ranks
          yearScores.sort((a, b) => b.score - a.score);
          
          // Add rank data for all managers
          yearScores.forEach((item, index) => {
            yearData[item.manager] = index + 1; // Rank 1 = highest score
          });
          
          // Get season details for context
          const seasonDetails = getSeasonDetails(year, selectedManager);
          yearData.seasonDetails = seasonDetails;
          
          return yearData;
        });

        // Filter out years where selected manager has no data
        const filteredChartData = chartDataByYear.filter(yearData => yearData[selectedManager] !== undefined);

        console.log('Processed rank chart data:', filteredChartData);

        setChartData(filteredChartData);
        setLoading(false);

      } catch (err) {
        console.error('Error processing legacy rank data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    processLegacyRankData();
  }, [selectedManager, legacyData, finalData, h2hData]);

  // Helper function to get season details for context
  const getSeasonDetails = (year, manager) => {
    const seasonRecord = finalData.find(row => 
      row.Year === year && 
      (row.Owner === manager || row.Manager === manager)
    );

    const seasonGames = h2hData.filter(row => 
      row.Year === year && 
      row.Manager1 === manager && 
      row.Type === 'Regular Season'
    );

    const wins = seasonGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);
    const losses = seasonGames.reduce((sum, game) => sum + (game.IsLoss || 0), 0);

    return {
      wins: wins,
      losses: losses,
      record: `${wins}-${losses}`,
      standings: seasonRecord ? seasonRecord.Standings : null,
      hasData: seasonRecord || seasonGames.length > 0
    };
  };

  // Calculate rank movements for annotations
  const getRankMovements = () => {
    if (!chartData.length || chartData.length < 2) return [];
    
    const movements = [];
    for (let i = 1; i < chartData.length; i++) {
      const prev = chartData[i - 1];
      const curr = chartData[i];
      const prevRank = prev[selectedManager];
      const currRank = curr[selectedManager];
      
      if (prevRank !== undefined && currRank !== undefined) {
        const movement = prevRank - currRank; // Positive = moved up, negative = moved down
        
        movements.push({
          year: curr.Year,
          rank: currRank,
          movement: movement,
          prevRank: prevRank,
          isSignificant: Math.abs(movement) >= 2, // Significant if moved 2+ ranks
          seasonDetails: curr.seasonDetails
        });
      }
    }

    return movements.filter(m => m.isSignificant);
  };

  const significantMovements = getRankMovements();

  const formatTooltip = (value, name, props) => {
    if (value === null || value === undefined) return null;
    
    const rankText = value === 1 ? '1st' : value === 2 ? '2nd' : value === 3 ? '3rd' : `${value}th`;
    
    if (name === selectedManager) {
      const yearData = chartData.find(d => d.Year === props.payload?.Year);
      const details = yearData?.seasonDetails;
      
      if (details && details.hasData && !isMobile) {
        return [
          `${rankText} Place`,
          `${selectedManager} (${details.record})`
        ];
      } else {
        return [`${rankText} Place`, selectedManager];
      }
    } else {
      return [`${rankText} Place`, name];
    }
  };

  const formatYAxis = (value) => {
    if (value === 1) return '1st';
    if (value === 2) return '2nd';
    if (value === 3) return '3rd';
    return `${value}th`;
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="mb-4 sm:mb-6">
          <h3 className={`font-bold text-gray-900 mb-2 ${isMobile ? 'text-lg' : 'text-xl lg:text-2xl'}`}>
            {isMobile ? `${selectedManager}'s Legacy Rank` : `${selectedManager}'s Legacy Score Rank Over Time`}
          </h3>
          <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {isMobile ? 'Ranking position by year' : 'Legacy score ranking position by year'}
          </p>
        </div>
        
        <div className="flex items-center justify-center" style={{ height: isMobile ? 300 : 400 }}>
          <div className="flex flex-col items-center space-y-3 sm:space-y-4">
            <div className={`border-4 border-gray-200 border-t-purple-600 rounded-full animate-spin ${
              isMobile ? 'w-8 h-8' : 'w-12 h-12'
            }`}></div>
            <span className={`text-gray-600 font-medium ${isMobile ? 'text-sm' : 'text-base'}`}>
              Loading legacy rank data...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="text-center text-red-500 py-8 sm:py-12">
          <div className={isMobile ? 'text-sm' : 'text-base'}>Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="text-center text-gray-500 py-8 sm:py-12">
          <div className={isMobile ? 'text-sm' : 'text-base'}>
            No legacy rank data found for {selectedManager}
          </div>
        </div>
      </div>
    );
  }

  // Get all managers for background visualization
  const allManagers = [...new Set(legacyData.map(row => row.Manager))];
  const otherManagers = allManagers.filter(manager => manager !== selectedManager);

  return (
    <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
      {/* Title - Mobile Optimized */}
      <div className="mb-4 sm:mb-6">
        <h3 className={`font-bold text-gray-900 mb-2 ${isMobile ? 'text-lg' : 'text-xl lg:text-2xl'}`}>
          {isMobile ? `${selectedManager}'s Legacy Rank` : `${selectedManager}'s Legacy Score Rank Over Time`}
        </h3>
        <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          {isMobile ? 'Ranking position by year (1st = best)' : 'Legacy score ranking position by year (1st = best)'}
        </p>
      </div>

      {/* Chart - Mobile Optimized */}
      <div style={{ width: '100%', height: isMobile ? 300 : 400 }}>
        <ResponsiveContainer>
          <LineChart 
            data={chartData}
            margin={{ 
              top: 20, 
              right: isMobile ? 15 : 30, 
              left: isMobile ? 25 : 20, 
              bottom: 20 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="Year" 
              tick={{ fontSize: isMobile ? 10 : 12, fill: '#6b7280' }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              interval={isMobile ? 1 : 0} // Show every other year on mobile
            />
            <YAxis 
              tickFormatter={formatYAxis}
              tick={{ fontSize: isMobile ? 10 : 12, fill: '#6b7280' }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              domain={[1, allManagers.length]}
              reversed={true}
              width={isMobile ? 35 : 50}
            />
            <Tooltip 
              formatter={formatTooltip}
              labelStyle={{ color: '#374151', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                fontSize: isMobile ? '12px' : '14px',
                padding: isMobile ? '8px' : '12px'
              }}
              itemSorter={(item) => item.value}
            />
            
            {/* Background lines and dots for all other managers - Simplified on mobile */}
            {!isMobile && otherManagers.map(manager => (
              <Line
                key={manager}
                type="monotone"
                dataKey={manager}
                stroke="#d1d5db"
                strokeWidth={2}
                strokeOpacity={0.3}
                dot={{ fill: '#d1d5db', fillOpacity: 0.4, stroke: 'none', r: 3 }}
                activeDot={false}
                connectNulls={false}
              />
            ))}
            
            {/* Main line for selected manager */}
            <Line
              type="monotone"
              dataKey={selectedManager}
              stroke="#8b5cf6"
              strokeWidth={isMobile ? 2.5 : 3}
              dot={(props) => {
                const { cx, cy, payload } = props;
                if (!payload) return null;
                
                const movement = significantMovements.find(m => m.year === payload.Year);
                if (movement && !isMobile) {
                  const isImprovement = movement.movement > 0;
                  return (
                    <g>
                      {/* Significant movement dot */}
                      <circle
                        cx={cx}
                        cy={cy}
                        r={8}
                        fill={isImprovement ? '#10b981' : '#ef4444'}
                        stroke="white"
                        strokeWidth={2}
                      />
                      {/* Movement annotation */}
                      <text
                        x={cx}
                        y={cy + 3}
                        textAnchor="middle"
                        fill="white"
                        fontSize="10"
                        fontWeight="bold"
                      >
                        {isImprovement ? '↑' : '↓'}
                      </text>
                    </g>
                  );
                } else {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={isMobile ? 4 : 6}
                      fill={movement && isMobile ? (movement.movement > 0 ? '#10b981' : '#ef4444') : '#8b5cf6'}
                      stroke="white"
                      strokeWidth={isMobile ? 1.5 : 2}
                    />
                  );
                }
              }}
              activeDot={{ r: isMobile ? 6 : 8, stroke: '#8b5cf6', strokeWidth: 2, fill: 'white' }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Significant Movements Summary - Mobile Optimized */}
      <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
        {significantMovements.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <h4 className={`font-semibold text-gray-700 mb-3 ${isMobile ? 'text-sm' : 'text-sm'}`}>
              Significant Rank Changes
            </h4>
            <div className={`grid gap-2 sm:gap-3 ${
              isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {significantMovements
                .sort((a, b) => Math.abs(b.movement) - Math.abs(a.movement))
                .slice(0, isMobile ? 3 : 6) // Show fewer on mobile
                .map((movement) => {
                  const isImprovement = movement.movement > 0;
                  const getRankText = (rank) => rank === 1 ? '1st' : rank === 2 ? '2nd' : rank === 3 ? '3rd' : `${rank}th`;
                  
                  return (
                    <div
                      key={movement.year}
                      className={`p-2 sm:p-3 rounded-lg border-l-4 ${
                        isImprovement
                          ? 'bg-green-50 border-green-500'
                          : 'bg-red-50 border-red-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium text-gray-700 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                          {movement.year}
                        </span>
                        <span
                          className={`font-bold ${isMobile ? 'text-sm' : 'text-sm'} ${
                            isImprovement ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {isImprovement ? '↑' : '↓'} {Math.abs(movement.movement)}
                        </span>
                      </div>
                      
                      {/* Season Details - Mobile Optimized */}
                      {movement.seasonDetails.hasData && (
                        <div className="mt-2 space-y-1">
                          <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                            <span className="font-medium">{movement.seasonDetails.record}</span>
                            {movement.seasonDetails.standings && (
                              <span className="ml-2">
                                • {movement.seasonDetails.standings === 1 ? (isMobile ? '1st 🏆' : '1st Place 🏆') : 
                                   movement.seasonDetails.standings === 2 ? (isMobile ? '2nd 🥈' : '2nd Place 🥈') : 
                                   movement.seasonDetails.standings === 3 ? (isMobile ? '3rd 🥉' : '3rd Place 🥉') : 
                                   movement.seasonDetails.standings >= 10 ? (isMobile ? `${movement.seasonDetails.standings}th 💩` : `${movement.seasonDetails.standings}th Place 💩`) : 
                                   (isMobile ? `${movement.seasonDetails.standings}th` : `${movement.seasonDetails.standings}th Place`)}
                              </span>
                            )}
                          </div>
                          <div className={`font-medium ${isMobile ? 'text-xs' : 'text-xs'} ${
                            isImprovement ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {isImprovement ? (isMobile ? 'Up to' : 'Climbed to') : (isMobile ? 'Down to' : 'Dropped to')} {getRankText(movement.rank)}
                          </div>
                        </div>
                      )}
                      
                      <div className={`text-gray-400 mt-1 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                        {getRankText(movement.prevRank)} → {getRankText(movement.rank)}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Overall Rank Stats - Mobile Optimized */}
        <div className={`grid gap-3 sm:gap-4 text-center ${
          isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'
        }`}>
          {(() => {
            const managerRanks = chartData.map(d => d[selectedManager]).filter(v => v !== null && v !== undefined);
            if (managerRanks.length === 0) return null;
            
            const currentRank = managerRanks[managerRanks.length - 1];
            
            // Find best rank and its most recent year
            const bestRank = Math.min(...managerRanks);
            const bestRankYear = chartData.slice().reverse().find(yearData => yearData[selectedManager] === bestRank)?.Year;
            
            // Find worst rank and its most recent year
            const worstRank = Math.max(...managerRanks);
            const worstRankYear = chartData.slice().reverse().find(yearData => yearData[selectedManager] === worstRank)?.Year;
            
            const avgRank = (managerRanks.reduce((sum, rank) => sum + rank, 0) / managerRanks.length).toFixed(1);
            
            const getRankText = (rank) => rank === 1 ? '1st' : rank === 2 ? '2nd' : rank === 3 ? '3rd' : `${rank}th`;
            
            return (
              <>
                <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                  <div className={`font-bold text-gray-900 ${isMobile ? 'text-base' : 'text-lg'}`}>
                    {getRankText(currentRank)}
                  </div>
                  <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                    Current Rank
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                  <div className={`font-bold text-green-600 ${isMobile ? 'text-base' : 'text-lg'}`}>
                    {getRankText(bestRank)}
                  </div>
                  <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                    Best Rank {!isMobile && bestRankYear ? `(${bestRankYear})` : ''}
                  </div>
                </div>
                {!isMobile && (
                  <>
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                      <div className={`font-bold text-red-600 ${isMobile ? 'text-base' : 'text-lg'}`}>
                        {getRankText(worstRank)}
                      </div>
                      <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                        Worst Rank {worstRankYear ? `(${worstRankYear})` : ''}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                      <div className={`font-bold text-gray-900 ${isMobile ? 'text-base' : 'text-lg'}`}>
                        {avgRank}
                      </div>
                      <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                        Average Rank
                      </div>
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default LegacyRankChart;